import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom";
import { useAuth } from "../provider/authProvider";
import { ProtectedRoute } from "./ProtectedRoute";
import * as Components from '../components/@index.js';
import * as global from '../data/global';

const Routes = () => {
    const { token } = useAuth();
    // Token in storage is not proof of a valid session; gw.isAuthenticated is set only after
    // validateToken (or login). Keeps routing aligned with ProtectedRoute and avoids a logged-in flash.
    const isAuthenticated =
        global.gw.isAuthenticated === true &&
        !!(token || (global.gw.token && global.gw.auth));

    // Define public routes accessible to all users
    const routesForPublic = [
        {
            path: "/service",
            element: <div>Service Page</div>,
        },
        {
            path: "/about-us",
            element: <div>About Us</div>,
        },
    ];

    // Define routes accessible only to authenticated users
    const routesForAuthenticatedOnly = [
        {
            path: "/",
            element: <ProtectedRoute />, // Wrap the component in ProtectedRoute
            children: [
                {
                    path: "/",
                    element: <Components.CustomerSites />,
                },
                {
                    path: "/sites",
                    element: <Components.CustomerSites />,
                },
                {
                    path: "/dashboard",
                    element: <Components.CustomerSite />,
                },
                {
                    path: "/profile",
                    element: <Components.Profile />,
                },
                {
                    path: "/logout",
                    element: <Components.Logout />,
                },
                {
                    path: "/site3d",
                    element: <div
                        name="Site 3D"
                    >
                        <Components.Site3dWindow />
                    </div>
                },
                {
                    path: "/temperatureComparison",
                    element: <div
                        name="Temperature Comparison"
                    >
                        <Components.ReportWindow />
                    </div>
                },
                {
                    path: "/carbonDioxide",
                    element: <div
                        name="CO2"
                    >
                        <Components.CarbonDioxideWindow />
                    </div>
                },
                {
                    path: "/temperatureHistory",
                    element: <div
                        name="Sensor History Graph"
                    >
                        <Components.SensorTemperatureGraphWindow />
                    </div>
                },
                {
                    path: "/scan",
                    element: <div
                        name="Scan"
                    >
                        <Components.ScanWindow />
                    </div>
                },
                {
                    path: "/diagnostics",
                    element: <div
                        name="Diagnostics"
                    >
                        <Components.DiagnosticsWindow />
                    </div>
                },
                {
                    path: "/moduleViewer",
                    element: <div
                        name="Module Viewer"
                    >
                        <Components.ModuleViewerWindow />
                    </div>
                },
                {
                    path: "/devices",
                    element: <div
                        name="Devices"
                    >
                        <Components.DevicesWindow />
                    </div>
                },
                {
                    path: "/inventory",
                    element: <div
                        name="Inventory"
                    >
                        <Components.InventoryWindow />
                    </div>
                },
                {
                    path: "/bin",
                    element: <div
                        name="Bin 3D"
                    >
                        <Components.BinWindow />
                    </div>
                },
                {
                    path: "/siteLayout",
                    element: <div name="Site Layout">
                        <Components.SiteLayout />
                    </div>
                },
                {
                    path: "/siteSetup",
                    element: <div name="Site Setup">
                        <Components.SiteSetupWindow />
                    </div>
                },
                {
                    path: "/ccr/:siteGuid/:assetTag?",
                    element: <div name="CCR">
                        <Components.CcrInstrumentWindow />
                    </div>
                },
                {
                    path: "/cableHealth",
                    element: <div name="Cable Health">
                        <Components.CableHealthWindow />
                    </div>
                },
                {
                    path: "/manufacturing",
                    element: <div name="Manufacturing">
                        <Components.ManufacturingWindow />
                    </div>
                },
                {
                    path: "/device/:manufacturedDeviceGuid",
                    element: <div name="Device">
                        <Components.ManufacturedDeviceView />
                    </div>
                },
                {
                    path: "/layoutImport",
                    element: <Navigate to="/siteLayout" replace />
                }
            ],
        },
    ];

    // Define routes accessible only to non-authenticated users
    const routesForNotAuthenticatedOnly = [
        {
            path: "/emailLogin",
            element: <Components.EmailLogin />
        },
        {
            path: "/loading",
            element: <Components.Loading />
        },
    ];

    // Combine and conditionally include routes based on authentication status
    // Use isAuthenticated instead of just token to handle token restoration
    // Always include emailLogin route to ensure it's accessible even if token exists but is invalid
    const router = createBrowserRouter([
        ...routesForPublic,
        // Always include emailLogin route through ProtectedRoute so it shows the AppBar
        {
            path: "/emailLogin",
            element: <ProtectedRoute />
        },
        ...(!isAuthenticated ? routesForNotAuthenticatedOnly.filter(route => route.path !== "/emailLogin") : []),
        ...routesForAuthenticatedOnly,
    ]);

    // Provide the router configuration using RouterProvider
    return <RouterProvider router={router} />;
};

export default Routes;